import {
  StoreIcon,
  LayoutTemplateIcon,
  BlocksIcon,
  LineChartIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface ServiceProcessStep {
  step: string
  title: string
  desc: string
}

export interface ServiceFAQ {
  q: string
  a: string
}

export interface ServiceStat {
  value: string
  label: string
}

export interface ServiceData {
  slug: string
  icon: LucideIcon
  eyebrow: string
  title: string
  heroHeadline: string
  heroSubhead: string
  // The problem we solve + the outcome we deliver
  problem: string
  outcome: string
  stats: ServiceStat[]
  // "What's included" deliverables
  deliverables: { title: string; desc: string }[]
  // Outcome-oriented benefit bullets
  benefits: string[]
  process: ServiceProcessStep[]
  faqs: ServiceFAQ[]
  testimonial: { quote: string; name: string; role: string }
  // Engagement / pricing framing
  engagement: { label: string; value: string; note: string }
}

export const SERVICES: ServiceData[] = [
  {
    slug: 'store-development',
    icon: StoreIcon,
    eyebrow: 'Shopify Store Development',
    title: 'Store Development',
    heroHeadline: 'High-converting Shopify stores, built to scale',
    heroSubhead:
      'We design and engineer premium Shopify storefronts tailored to your brand, your customers, and your revenue goals. Fast, beautiful, and built to convert.',
    problem:
      'Most stores leak revenue: slow pages, confusing navigation, and checkout friction quietly kill conversions before customers ever reach “Buy”.',
    outcome:
      'We build a store that loads fast, guides shoppers effortlessly to checkout, and turns more of your existing traffic into paying customers.',
    stats: [
      { value: '1,000+', label: 'Stores delivered' },
      { value: '+38%', label: 'Avg. conversion lift' },
      { value: '< 2s', label: 'Target load time' },
      { value: '4.9★', label: 'Client rating' },
    ],
    deliverables: [
      {
        title: 'Custom storefront design',
        desc: 'A fully branded, conversion-focused design system, not a recycled template.',
      },
      {
        title: 'Product & collection pages',
        desc: 'Optimized PDP and PLP layouts engineered to drive add-to-carts.',
      },
      {
        title: 'Checkout optimization',
        desc: 'Reduced friction and abandonment with a streamlined path to purchase.',
      },
      {
        title: 'Technical SEO setup',
        desc: 'Clean structure, metadata, and schema so you rank and get found.',
      },
      {
        title: 'Performance tuning',
        desc: 'Speed and Core Web Vitals optimization for faster, higher-ranking pages.',
      },
      {
        title: 'Apps & integrations',
        desc: 'Email, reviews, analytics, and the tools your stack needs, wired in cleanly.',
      },
    ],
    benefits: [
      'A store designed around your customer’s buying journey',
      'Mobile-first design where most of your sales happen',
      'Conversion-rate best practices baked into every page',
      'Clean, maintainable code that scales with your growth',
    ],
    process: [
      {
        step: '01',
        title: 'Discovery & strategy',
        desc: 'We learn your brand, audience, and goals, then map the highest-impact opportunities.',
      },
      {
        step: '02',
        title: 'Design',
        desc: 'We design a premium, conversion-focused storefront and validate it with you.',
      },
      {
        step: '03',
        title: 'Build & QA',
        desc: 'We engineer it for speed and stability, then rigorously test across devices.',
      },
      {
        step: '04',
        title: 'Launch & optimize',
        desc: 'We launch, monitor, and fine-tune to keep conversions climbing.',
      },
    ],
    faqs: [
      {
        q: 'How long does a store build take?',
        a: 'Most premium store builds ship in 3–6 weeks depending on scope. We’ll give you a precise timeline after discovery.',
      },
      {
        q: 'Can you migrate my existing store?',
        a: 'Yes. We handle migrations from other platforms or older Shopify themes with zero data loss and proper redirects to protect your SEO.',
      },
      {
        q: 'Do you offer ongoing support?',
        a: 'Absolutely. We offer post-launch care plans for updates, optimization, and new features as you grow.',
      },
    ],
    testimonial: {
      quote:
        'They rebuilt our store and our conversion rate jumped almost immediately. It finally feels like the premium brand we are.',
      name: 'Jordan P.',
      role: 'Founder, Lifestyle Brand',
    },
    engagement: {
      label: 'Starting from',
      value: 'Custom quote',
      note: 'Scoped to your goals. Book a free consultation for an exact proposal.',
    },
  },
  {
    slug: 'theme-development',
    icon: LayoutTemplateIcon,
    eyebrow: 'Shopify Theme Development',
    title: 'Theme Development',
    heroHeadline: 'Custom Shopify themes engineered for speed & scale',
    heroSubhead:
      'Bespoke, lightning-fast Shopify themes built to Shopify’s strictest standards. Flexible for your team, blazing for your customers.',
    problem:
      'Off-the-shelf themes get bloated, slow, and rigid fast, and fighting the theme costs you speed, rankings, and developer hours.',
    outcome:
      'We build a clean, modular theme that’s fast out of the box, easy for your team to manage, and ready to scale with your catalog.',
    stats: [
      { value: '90+', label: 'Lighthouse target' },
      { value: '100%', label: 'Shopify-standard code' },
      { value: '∞', label: 'Scalable sections' },
      { value: '4.9★', label: 'Client rating' },
    ],
    deliverables: [
      {
        title: 'Modular section architecture',
        desc: 'Reusable, drag-and-drop sections your team can rearrange without code.',
      },
      {
        title: 'Speed optimization',
        desc: 'Lean Liquid, lazy-loading, and asset optimization for top Core Web Vitals.',
      },
      {
        title: 'Advanced customization',
        desc: 'Powerful theme settings so you control design without touching code.',
      },
      {
        title: 'Mobile-first build',
        desc: 'Pixel-perfect, fast experiences on the devices most of your customers use.',
      },
      {
        title: 'Marketplace-ready quality',
        desc: 'Built to the same standards Shopify requires for the Theme Store.',
      },
      {
        title: 'Documentation',
        desc: 'Clear handover docs so your team can run with it confidently.',
      },
    ],
    benefits: [
      'Themes that stay fast as your store grows',
      'No more fighting a bloated, rigid template',
      'Editable by your team, no developer needed for everyday changes',
      'Clean code that’s a joy to maintain and extend',
    ],
    process: [
      {
        step: '01',
        title: 'Architecture',
        desc: 'We plan a modular, scalable section structure around your needs.',
      },
      {
        step: '02',
        title: 'Design system',
        desc: 'We craft a cohesive, premium design language for your brand.',
      },
      {
        step: '03',
        title: 'Development',
        desc: 'We build lean, standards-compliant Liquid optimized for speed.',
      },
      {
        step: '04',
        title: 'QA & handover',
        desc: 'We test everywhere, document everything, and hand over cleanly.',
      },
    ],
    faqs: [
      {
        q: 'Will my team be able to edit the theme?',
        a: 'Yes. We build rich theme settings and modular sections so your team can customize layouts and content without writing code.',
      },
      {
        q: 'Can you match our existing brand?',
        a: 'Definitely. We translate your brand guidelines into a cohesive, premium theme design system.',
      },
      {
        q: 'Do you build themes for the Shopify Theme Store?',
        a: 'Yes. We build to Shopify’s marketplace standards, whether for your own store or for submission.',
      },
    ],
    testimonial: {
      quote:
        'Our new theme is dramatically faster and our team can finally update the site themselves. Worth every penny.',
      name: 'Lena M.',
      role: 'Ecommerce Lead',
    },
    engagement: {
      label: 'Starting from',
      value: 'Custom quote',
      note: 'Priced by complexity. Get a free scope and proposal.',
    },
  },
  {
    slug: 'app-development',
    icon: BlocksIcon,
    eyebrow: 'Shopify App Development',
    title: 'App Development',
    heroHeadline: 'Custom Shopify apps that unlock new capabilities',
    heroSubhead:
      'Public or private Shopify apps engineered to automate workflows, add features, and grow revenue, built by developers who ship to the App Store.',
    problem:
      'When Shopify can’t do what your business needs, manual workarounds pile up, costing time, introducing errors, and capping your growth.',
    outcome:
      'We build a reliable, secure app that automates the work, extends your store’s capabilities, and scales with your operation.',
    stats: [
      { value: 'Live', label: 'Apps on the App Store' },
      { value: '24/7', label: 'Automation' },
      { value: 'Secure', label: 'Shopify-compliant' },
      { value: '4.9★', label: 'Client rating' },
    ],
    deliverables: [
      {
        title: 'Public or private apps',
        desc: 'App Store-ready public apps or tailored private apps for your store.',
      },
      {
        title: 'Workflow automation',
        desc: 'Automate repetitive operations and eliminate manual busywork.',
      },
      {
        title: 'Advanced analytics',
        desc: 'Custom dashboards that surface the metrics that matter to you.',
      },
      {
        title: 'Upsell & cross-sell systems',
        desc: 'Revenue-driving merchandising logic built into your store.',
      },
      {
        title: 'Third-party integrations',
        desc: 'Connect Shopify to your ERP, CRM, fulfillment, and more.',
      },
      {
        title: 'App Bridge & best practices',
        desc: 'Native, secure, and compliant with Shopify’s latest standards.',
      },
    ],
    benefits: [
      'Replace fragile manual processes with reliable automation',
      'Capabilities tailored exactly to your business',
      'Secure, scalable, and built to Shopify’s standards',
      'A development partner who ships and supports',
    ],
    process: [
      {
        step: '01',
        title: 'Scoping',
        desc: 'We define the problem, requirements, and success metrics together.',
      },
      {
        step: '02',
        title: 'Architecture',
        desc: 'We design a secure, scalable technical approach.',
      },
      {
        step: '03',
        title: 'Build & test',
        desc: 'We develop iteratively with thorough testing and your feedback.',
      },
      {
        step: '04',
        title: 'Launch & maintain',
        desc: 'We deploy, monitor, and support the app as you grow.',
      },
    ],
    faqs: [
      {
        q: 'Can you build a private app just for my store?',
        a: 'Yes. We build both private (custom) apps for a single store and public apps intended for the Shopify App Store.',
      },
      {
        q: 'Will the app stay compliant with Shopify updates?',
        a: 'We build to current Shopify standards (App Bridge, APIs, security) and offer maintenance plans to keep it compliant over time.',
      },
      {
        q: 'Can you integrate with our other systems?',
        a: 'Absolutely. ERPs, CRMs, fulfillment, and custom APIs are all within scope.',
      },
    ],
    testimonial: {
      quote:
        'They built us a private app that automated a process eating hours every day. Flawless execution and great communication.',
      name: 'Samir D.',
      role: 'Operations Director',
    },
    engagement: {
      label: 'Starting from',
      value: 'Custom quote',
      note: 'Every app is unique. Book a call for a tailored estimate.',
    },
  },
  {
    slug: 'conversion-optimization',
    icon: LineChartIcon,
    eyebrow: 'Conversion Rate Optimization',
    title: 'Conversion Optimization',
    heroHeadline: 'Turn more of your existing traffic into revenue',
    heroSubhead:
      'Data-driven CRO that finds where your store leaks sales, then systematically fixes it with testing, not guesswork.',
    problem:
      'You’re paying to drive traffic, but most visitors leave without buying. Without data, “fixes” are just expensive guesses.',
    outcome:
      'We diagnose exactly where shoppers drop off and run a structured optimization program that lifts conversion rate and average order value.',
    stats: [
      { value: '+38%', label: 'Avg. conversion lift' },
      { value: 'A/B', label: 'Tested, not guessed' },
      { value: '+AOV', label: 'Higher order value' },
      { value: '4.9★', label: 'Client rating' },
    ],
    deliverables: [
      {
        title: 'Conversion audit',
        desc: 'A deep teardown of your funnel to pinpoint exactly where you lose sales.',
      },
      {
        title: 'User behavior analysis',
        desc: 'Heatmaps, session insights, and analytics to understand real behavior.',
      },
      {
        title: 'A/B testing program',
        desc: 'Structured experiments so every change is validated by data.',
      },
      {
        title: 'Checkout optimization',
        desc: 'Reduce abandonment with a frictionless path to purchase.',
      },
      {
        title: 'Funnel improvements',
        desc: 'Optimize every step from landing to thank-you page.',
      },
      {
        title: 'AOV strategies',
        desc: 'Upsells, bundles, and merchandising that grow order value.',
      },
    ],
    benefits: [
      'More revenue from the traffic you already pay for',
      'Decisions backed by data, not opinions',
      'A repeatable optimization engine, not one-off tweaks',
      'Clear reporting on exactly what moved the needle',
    ],
    process: [
      {
        step: '01',
        title: 'Audit',
        desc: 'We analyze your funnel and quantify where and why you’re losing sales.',
      },
      {
        step: '02',
        title: 'Hypotheses',
        desc: 'We prioritize the highest-impact opportunities into a test roadmap.',
      },
      {
        step: '03',
        title: 'Test',
        desc: 'We run controlled A/B tests to validate each improvement.',
      },
      {
        step: '04',
        title: 'Scale wins',
        desc: 'We roll out winners and compound the gains over time.',
      },
    ],
    faqs: [
      {
        q: 'How much can I expect to improve?',
        a: 'Results vary by store, but our clients commonly see double-digit conversion lifts. We focus on compounding, validated wins rather than one-off spikes.',
      },
      {
        q: 'Do I need a lot of traffic for CRO?',
        a: 'More traffic speeds up testing, but we tailor the approach to your volume, including qualitative methods when traffic is lower.',
      },
      {
        q: 'Is this a one-time project or ongoing?',
        a: 'CRO works best as an ongoing program. We offer both an initial audit and continuous optimization retainers.',
      },
    ],
    testimonial: {
      quote:
        'Their CRO program paid for itself in the first month. We finally know what works instead of guessing.',
      name: 'Priya N.',
      role: 'Growth Manager',
    },
    engagement: {
      label: 'Starting from',
      value: 'Audit + retainer',
      note: 'Start with an audit, then scale with ongoing optimization.',
    },
  },
]

export function getServiceBySlug(slug?: string): ServiceData | undefined {
  return SERVICES.find((s) => s.slug === slug)
}
